import { createElement, useEffect, useRef, useState, type ReactNode } from "react";
import { Part, type Code, type PartProps } from "./entrypoint.ts";

interface LaterProps<M> {
  readonly parts: Code<M>;
  readonly wait?: boolean;
  readonly height?: string;
  readonly children: (module: M) => ReactNode;
}

const ahead = "0px 0px 50% 0px";

export function Later<M>({ parts, wait = false, height, children }: LaterProps<M>): ReactNode {
  const [near, setNear] = useState(!wait);
  const place = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (near) return;
    const element = place.current;
    if (!element || typeof IntersectionObserver === "undefined") return setNear(true);
    const seen = (entries: readonly IntersectionObserverEntry[]) => entries.some((entry) => entry.isIntersecting) && setNear(true);
    const observer = new IntersectionObserver(seen, { rootMargin: ahead });
    observer.observe(element);
    return () => observer.disconnect();
  }, [near]);
  const waiting = height === undefined ? null : createElement("div", { ref: place, style: { blockSize: height }, "aria-hidden": "true" });
  return createElement<PartProps<M>>(Part, { parts, near, waiting, children });
}
